import { lazy, Suspense } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate, createRoutesFromElements,
} from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AnalyticsProvider } from '@/components/providers/AnalyticsProvider';
import { ConfirmHost, PageSpinner, Toaster, TooltipProvider } from '@/components/ui';
import { lazyPage } from '@/lib/lazyPage';
import { preloadRoute, registerRoutes } from '@/lib/routePreload';

/*
 * EVERY PAGE LOADS ON DEMAND. Each page below is its own file, fetched the first
 * time it is opened (src/lib/lazyPage.ts), so a visit downloads the app frame
 * and the page being opened rather than the whole of Flow — the outside
 * parties who open an NDA or an upload link included.
 *
 * The exceptions are the four sign-in pages, imported normally. They are the
 * first thing a signed-out visitor needs, and two of them receive one-time
 * tokens in the URL (a password-reset link, a Microsoft redirect) that are
 * consumed on arrival: the reload-once recovery that rescues a stale page after
 * a deploy would arrive without them.
 */

// Dev-only fixture gallery for UI states that need no sign-in and no Supabase.
// import.meta.env.DEV is replaced with a literal at build time, so in a
// production build this folds to null and the chunk is never emitted.
const DevFixtures = import.meta.env.DEV ? lazy(() => import('@/dev/DevFixtures')) : null;
// Dev-only switch for looking at the signed-in shell without signing in (see
// src/dev/devShell.ts). Same DEV fold as above: absent from production builds.
const DevShellEntry = import.meta.env.DEV ? lazy(() => import('@/dev/DevShellEntry')) : null;

// The researcher portal is its own lazy chunk: the internal bundle stays out of
// a contractor's normal load path. A courtesy and a size win, not a security
// boundary — protection lives in RLS and portal-api's field whitelists.
const PortalApp = lazyPage(() => import('@/portal/PortalApp'));
// Where "View as ↗" links land: a redirect into the portal with the researcher
// id, which the portal commits to the tab and renders itself in preview.
const PortalPreviewEntry = lazyPage(() => import('@/portal/PortalPreviewEntry'), 'PortalPreviewEntry');

// Auth Pages — loaded with the app, not on demand (see the note above)
import { Login } from '@/pages/auth/Login';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { MicrosoftCallback } from '@/pages/auth/MicrosoftCallback';

// Main Pages
const Dashboard = lazyPage(() => import('@/pages/dashboard/Dashboard'), 'Dashboard');
const ActivityFeed = lazyPage(() => import('@/pages/activity/ActivityFeed'), 'ActivityFeed');

// Deal Pages
const DealsPage = lazyPage(() => import('@/pages/deals/DealsPage'), 'DealsPage');
const DealNew = lazyPage(() => import('@/pages/deals/DealNew'), 'DealNew');
const DealDetail = lazyPage(() => import('@/pages/deals/DealDetail'), 'DealDetail');
const DealEdit = lazyPage(() => import('@/pages/deals/DealEdit'), 'DealEdit');

// Fund Pages
const FundsPage = lazyPage(() => import('@/pages/funds/FundsPage'), 'FundsPage');
const FundNew = lazyPage(() => import('@/pages/funds/FundNew'), 'FundNew');
const FundDetail = lazyPage(() => import('@/pages/funds/FundDetail'), 'FundDetail');
const FundEdit = lazyPage(() => import('@/pages/funds/FundEdit'), 'FundEdit');

// Property Pages
const PropertyNew = lazyPage(() => import('@/pages/properties/PropertyNew'), 'PropertyNew');
const PropertyDetail = lazyPage(() => import('@/pages/properties/PropertyDetail'), 'PropertyDetail');
const PropertyEdit = lazyPage(() => import('@/pages/properties/PropertyEdit'), 'PropertyEdit');
const PropertyCompare = lazyPage(() => import('@/pages/properties/PropertyCompare'), 'PropertyCompare');
const PropertyListsPage = lazyPage(() => import('@/pages/property-lists/PropertyListsPage'), 'PropertyListsPage');
const PortfolioPage = lazyPage(() => import('@/pages/portfolio/PortfolioPage'), 'PortfolioPage');
const SkipTraceHistory = lazyPage(() => import('@/pages/skip-traces/SkipTraceHistory'), 'SkipTraceHistory');

// Source Pages (list folded into Contacts; detail/edit kept for deep links)
const SourceDetail = lazyPage(() => import('@/pages/sources/SourceDetail'), 'SourceDetail');
const SourceNew = lazyPage(() => import('@/pages/sources/SourceNew'), 'SourceNew');
const SourceEdit = lazyPage(() => import('@/pages/sources/SourceEdit'), 'SourceEdit');

// Offer Pages
const OffersList = lazyPage(() => import('@/pages/offers/OffersList'), 'OffersList');
const OfferDetail = lazyPage(() => import('@/pages/offers/OfferDetail'), 'OfferDetail');
const OfferNew = lazyPage(() => import('@/pages/offers/OfferNew'), 'OfferNew');
const OfferEdit = lazyPage(() => import('@/pages/offers/OfferEdit'), 'OfferEdit');

// Financing Pages
const FinancingOverview = lazyPage(() => import('@/pages/financing/FinancingOverview'), 'FinancingOverview');
const FinancingDetail = lazyPage(() => import('@/pages/financing/FinancingDetail'), 'FinancingDetail');
const FinancingEdit = lazyPage(() => import('@/pages/financing/FinancingEdit'), 'FinancingEdit');
const LendersList = lazyPage(() => import('@/pages/financing/LendersList'), 'LendersList');
const LenderDetail = lazyPage(() => import('@/pages/financing/LenderDetail'), 'LenderDetail');
const LenderNew = lazyPage(() => import('@/pages/financing/LenderNew'), 'LenderNew');
const LenderEdit = lazyPage(() => import('@/pages/financing/LenderEdit'), 'LenderEdit');

// Execution Pages
const TasksList = lazyPage(() => import('@/pages/tasks/TasksList'), 'TasksList');
const TaskDetail = lazyPage(() => import('@/pages/tasks/TaskDetail'), 'TaskDetail');
const TaskNew = lazyPage(() => import('@/pages/tasks/TaskNew'), 'TaskNew');
const TaskEdit = lazyPage(() => import('@/pages/tasks/TaskEdit'), 'TaskEdit');
const DiligenceOverview = lazyPage(() => import('@/pages/diligence/DiligenceOverview'), 'DiligenceOverview');

// Pipeline Pages
const InboundLeadsPage = lazyPage(() => import('@/pages/pipeline/InboundLeadsPage'), 'InboundLeadsPage');

// Feature Log
const FeatureLog = lazyPage(() => import('@/pages/features/FeatureLog'), 'FeatureLog');

// Admin
const AdminCenter = lazyPage(() => import('@/pages/admin/AdminCenter'), 'AdminCenter');
const AIImportFeedback = lazyPage(() => import('@/pages/admin/AIImportFeedback'), 'AIImportFeedback');

// Insights Pages
const ReportsOverview = lazyPage(() => import('@/pages/reports/ReportsOverview'), 'ReportsOverview');
const OffersReport = lazyPage(() => import('@/pages/reports/OffersReport'), 'OffersReport');
const ResearchDashboard = lazyPage(() => import('@/pages/research/ResearchDashboard'), 'ResearchDashboard');
const DevelopmentOpportunities = lazyPage(() => import('@/pages/research/DevelopmentOpportunities'), 'DevelopmentOpportunities');
const ParcelWatch = lazyPage(() => import('@/pages/research/ParcelWatch'), 'ParcelWatch');
const OpsLayout = lazyPage(() => import('@/pages/ops/OpsLayout'), 'OpsLayout');
const OpsDashboard = lazyPage(() => import('@/pages/ops/OpsDashboard'), 'OpsDashboard');
const OpsResearchers = lazyPage(() => import('@/pages/ops/OpsResearchers'), 'OpsResearchers');
const ResearcherDetail = lazyPage(() => import('@/pages/ops/ResearcherDetail'), 'ResearcherDetail');
const OpsActivity = lazyPage(() => import('@/pages/ops/OpsActivity'), 'OpsActivity');
const OpsActivityFeed = lazyPage(() => import('@/pages/ops/OpsActivityFeed'), 'OpsActivityFeed');
const OpsChat = lazyPage(() => import('@/pages/ops/OpsChat'), 'OpsChat');
const OpsPropertyRedirect = lazyPage(() => import('@/pages/ops/OpsPropertyRedirect'), 'OpsPropertyRedirect');
const TerritoriesAdmin = lazyPage(() => import('@/pages/ops/TerritoriesAdmin'), 'TerritoriesAdmin');
const OpsReview = lazyPage(() => import('@/pages/ops/OpsReview'), 'OpsReview');
const OpsAssignments = lazyPage(() => import('@/pages/ops/OpsAssignments'), 'OpsAssignments');
const ResearcherPortalView = lazyPage(() => import('@/pages/ops/ResearcherPortalView'), 'ResearcherPortalView');
const StaffCallCenter = lazyPage(() => import('@/pages/ops/StaffCallCenter'), 'StaffCallCenter');
const OpsNotes = lazyPage(() => import('@/pages/ops/OpsNotes'), 'OpsNotes');
const OpsOffers = lazyPage(() => import('@/pages/ops/OpsOffers'), 'OpsOffers');
const OpsLetters = lazyPage(() => import('@/pages/ops/OpsLetters'), 'OpsLetters');
const OpsFinancials = lazyPage(() => import('@/pages/ops/OpsFinancials'), 'OpsFinancials');
const OpsNdas = lazyPage(() => import('@/pages/ops/OpsNdas'), 'OpsNdas');

// AI Imports
const AIImportsPage = lazyPage(() => import('@/pages/ai-imports/AIImportsPage'), 'AIImportsPage');
const AIImportDetail = lazyPage(() => import('@/pages/ai-imports/AIImportDetail'), 'AIImportDetail');
const ListingReviewQueue = lazyPage(() => import('@/pages/ai-imports/ListingReviewQueue'), 'ListingReviewQueue');
const EmailActivityPage = lazyPage(() => import('@/pages/email/EmailActivityPage'), 'EmailActivityPage');

// Public, no-login shared analysis
const SharedAnalysis = lazyPage(() => import('@/pages/shared/SharedAnalysis'), 'SharedAnalysis');

// Public, no-login seller document upload and NDA signing
const FinancialsUpload = lazyPage(() => import('@/pages/public/FinancialsUpload'), 'FinancialsUpload');
const NdaSign = lazyPage(() => import('@/pages/public/NdaSign'), 'NdaSign');

// Contacts Pages
const ContactsList = lazyPage(() => import('@/pages/contacts/ContactsList'), 'ContactsList');
const ContactDetail = lazyPage(() => import('@/pages/contacts/ContactDetail'), 'ContactDetail');
const ContactNew = lazyPage(() => import('@/pages/contacts/ContactNew'), 'ContactNew');
const ContactEdit = lazyPage(() => import('@/pages/contacts/ContactEdit'), 'ContactEdit');
const ContactTriage = lazyPage(() => import('@/pages/contacts/ContactTriage'), 'ContactTriage');

// Markets Pages
const MarketsWatchlist = lazyPage(() => import('@/pages/markets/MarketsWatchlist'), 'MarketsWatchlist');
const MarketDetail = lazyPage(() => import('@/pages/markets/MarketDetail'), 'MarketDetail');
const MarketReviewQueue = lazyPage(() => import('@/pages/markets/MarketReviewQueue'), 'MarketReviewQueue');
const MarketComparison = lazyPage(() => import('@/pages/markets/MarketComparison'), 'MarketComparison');

// Marketing Pages
const OwnerEvidenceQueue = lazyPage(() => import('@/pages/owners/OwnerEvidenceQueue'), 'OwnerEvidenceQueue');
const OwnersDirectory = lazyPage(() => import('@/pages/owners/OwnersDirectory'), 'OwnersDirectory');
const MarketingPage = lazyPage(() => import('@/pages/marketing/MarketingPage'), 'MarketingPage');
const MarketingCampaignDetail = lazyPage(() => import('@/pages/marketing/CampaignDetail'), 'CampaignDetail');
const OutreachQueue = lazyPage(() => import('@/pages/marketing/OutreachQueue'), 'OutreachQueue');
const OutreachPrintRun = lazyPage(() => import('@/pages/marketing/OutreachPrintRun'), 'OutreachPrintRun');

// Settings Pages
const SettingsOverview = lazyPage(() => import('@/pages/settings/SettingsOverview'), 'SettingsOverview');
const ProfileSettings = lazyPage(() => import('@/pages/settings/ProfileSettings'), 'ProfileSettings');
const UserManagement = lazyPage(() => import('@/pages/settings/UserManagement'), 'UserManagement');
const InvestmentCriteria = lazyPage(() => import('@/pages/settings/InvestmentCriteria'), 'InvestmentCriteria');
const TaskTemplates = lazyPage(() => import('@/pages/settings/TaskTemplates'), 'TaskTemplates');
const AuditLog = lazyPage(() => import('@/pages/settings/AuditLog'), 'AuditLog');
const UserActivity = lazyPage(() => import('@/pages/settings/UserActivity'), 'UserActivity');
const SettingsAnalyticsDashboard = lazyPage(() => import('@/pages/settings/AnalyticsDashboard'), 'AnalyticsDashboard');
const FeedbackManagement = lazyPage(() => import('@/pages/settings/FeedbackManagement'), 'FeedbackManagement');
const EmailTemplates = lazyPage(() => import('@/pages/settings/EmailTemplates'), 'EmailTemplates');
const EmailIntake = lazyPage(() => import('@/pages/settings/EmailIntake'), 'EmailIntake');

// Help Pages
const HelpOverview = lazyPage(() => import('@/pages/help/HelpOverview'), 'HelpOverview');
const FAQ = lazyPage(() => import('@/pages/help/FAQ'), 'FAQ');
const Glossary = lazyPage(() => import('@/pages/help/Glossary'), 'Glossary');
const FeatureGuide = lazyPage(() => import('@/pages/help/FeatureGuide'), 'FeatureGuide');
const DashboardGuide = lazyPage(() => import('@/pages/help/guides/DashboardGuide'), 'DashboardGuide');
const PropertiesGuide = lazyPage(() => import('@/pages/help/guides/PropertiesGuide'), 'PropertiesGuide');
const ContactsGuide = lazyPage(() => import('@/pages/help/guides/ContactsGuide'), 'ContactsGuide');
const OffersGuide = lazyPage(() => import('@/pages/help/guides/OffersGuide'), 'OffersGuide');
const FinancingGuide = lazyPage(() => import('@/pages/help/guides/FinancingGuide'), 'FinancingGuide');
const TasksGuide = lazyPage(() => import('@/pages/help/guides/TasksGuide'), 'TasksGuide');
const DiligenceGuide = lazyPage(() => import('@/pages/help/guides/DiligenceGuide'), 'DiligenceGuide');
const ICMeetingsGuide = lazyPage(() => import('@/pages/help/guides/ICMeetingsGuide'), 'ICMeetingsGuide');
const AnalyticsGuide = lazyPage(() => import('@/pages/help/guides/AnalyticsGuide'), 'AnalyticsGuide');
const SourcesGuide = lazyPage(() => import('@/pages/help/guides/SourcesGuide'), 'SourcesGuide');
const MarketsGuide = lazyPage(() => import('@/pages/help/guides/MarketsGuide'), 'MarketsGuide');
const DealsGuide = lazyPage(() => import('@/pages/help/guides/DealsGuide'), 'DealsGuide');
const FundsGuide = lazyPage(() => import('@/pages/help/guides/FundsGuide'), 'FundsGuide');
const BuyBoxGuide = lazyPage(() => import('@/pages/help/guides/BuyBoxGuide'), 'BuyBoxGuide');
const PortfolioGuide = lazyPage(() => import('@/pages/help/guides/PortfolioGuide'), 'PortfolioGuide');
const FinancialModelingGuide = lazyPage(() => import('@/pages/help/guides/FinancialModelingGuide'), 'FinancialModelingGuide');
const PropertyChecklistGuide = lazyPage(() => import('@/pages/help/guides/PropertyChecklistGuide'), 'PropertyChecklistGuide');
const OutreachGuide = lazyPage(() => import('@/pages/help/guides/OutreachGuide'), 'OutreachGuide');
const AIImportGuide = lazyPage(() => import('@/pages/help/guides/AIImportGuide'), 'AIImportGuide');
const AIAssistantGuide = lazyPage(() => import('@/pages/help/guides/AIAssistantGuide'), 'AIAssistantGuide');
const StarRatingsGuide = lazyPage(() => import('@/pages/help/guides/StarRatingsGuide'), 'StarRatingsGuide');
const OwnerIntelligenceGuide = lazyPage(() => import('@/pages/help/guides/OwnerIntelligenceGuide'), 'OwnerIntelligenceGuide');
const ResearchOpsGuide = lazyPage(() => import('@/pages/help/guides/ResearchOpsGuide'), 'ResearchOpsGuide');
const PropertyListsGuide = lazyPage(() => import('@/pages/help/guides/PropertyListsGuide'), 'PropertyListsGuide');
const DocumentsPhotosGuide = lazyPage(() => import('@/pages/help/guides/DocumentsPhotosGuide'), 'DocumentsPhotosGuide');
const SharingGuide = lazyPage(() => import('@/pages/help/guides/SharingGuide'), 'SharingGuide');
const DevelopmentOpportunitiesGuide = lazyPage(() => import('@/pages/help/guides/DevelopmentOpportunitiesGuide'), 'DevelopmentOpportunitiesGuide');

const appRoutes = (
  <>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/callback" element={<MicrosoftCallback />} />

    {/* Public, no-login shared analysis — deliberately OUTSIDE AppLayout so
        a third party sees only this one analysis and nothing else. */}
    <Route path="/shared/analysis/:token" element={<SharedAnalysis />} />

    {/* Public, no-login seller upload — OUTSIDE AppLayout for the same
        reason as the shared analysis: a seller sees this one page and
        nothing else. The token is the only credential. */}
    <Route path="/documents/:token" element={<FinancialsUpload />} />

    {/* Public, no-login NDA signing — OUTSIDE AppLayout for the same reason:
        a seller sees this one agreement and nothing else. The token in the
        path is the only credential; the page sends it in POST bodies only. */}
    <Route path="/sign/:token" element={<NdaSign />} />

    {/* External researcher portal — deliberately OUTSIDE AppLayout, with
        its own gate against check_portal_access(). */}
    <Route
      path="/portal/*"
      element={<PortalApp />}
    />

    {/* Staff View-As in its own window: hands the researcher id to the REAL
        portal, which renders itself read-only for staff (see
        src/portal/lib/previewMode.ts). The old replica of the portal chrome
        is gone — it drifted the day the portal changed. */}
    <Route path="/portal-preview/:id" element={<PortalPreviewEntry />} />

    {DevFixtures && (
      <Route
        path="/__dev/fixtures"
        element={
          <Suspense fallback={null}>
            <DevFixtures />
          </Suspense>
        }
      />
    )}

    {DevShellEntry && (
      <Route
        path="/__dev/shell"
        element={
          <Suspense fallback={null}>
            <DevShellEntry />
          </Suspense>
        }
      />
    )}

    {/* Protected Routes */}
    <Route path="/" element={<AppLayout />}>
      {/* Dashboard */}
      <Route index element={<Dashboard />} />

      {/* Activity Feed */}
      <Route path="activity" element={<ActivityFeed />} />

      {/* Funds */}
      <Route path="funds" element={<FundsPage />} />
      <Route path="funds/new" element={<FundNew />} />
      <Route path="funds/:id" element={<FundDetail />} />
      <Route path="funds/:id/edit" element={<FundEdit />} />

      {/* Deals */}
      <Route path="deals" element={<DealsPage />} />
      <Route path="deals/new" element={<DealNew />} />
      <Route path="deals/:id" element={<DealDetail />} />
      <Route path="deals/:id/edit" element={<DealEdit />} />

      {/* Pipeline — the Buy Box / Hit List page was folded into Property
          Lists (the "Hit List" tab). Redirect old links + bookmarks. */}
      <Route path="pipeline/buy-box" element={<Navigate to="/property-lists" replace />} />
      <Route path="pipeline/inbound-leads" element={<InboundLeadsPage />} />

      {/* Properties — the legacy list is retired; redirect to Property Lists.
          Detail/edit/analysis routes stay for deep links. */}
      <Route path="properties" element={<Navigate to="/property-lists" replace />} />
      <Route path="properties/new" element={<PropertyNew />} />
      <Route path="properties/compare" element={<PropertyCompare />} />
      <Route path="properties/:id" element={<PropertyDetail />} />
      <Route path="properties/:id/edit" element={<PropertyEdit />} />

      {/* Property Lists */}
      <Route path="property-lists" element={<PropertyListsPage />} />

      {/* Portfolio — our Endeavor and JRB parks, and the market ring around each */}
      <Route path="portfolio" element={<PortfolioPage />} />

      {/* Skip Trace history — chronological log of owner traces */}
      <Route path="skip-traces" element={<SkipTraceHistory />} />

      {/* Sources — list folded into Contacts; detail/edit kept for deep links */}
      <Route path="sources" element={<Navigate to="/contacts" replace />} />
      <Route path="sources/new" element={<SourceNew />} />
      <Route path="sources/:id" element={<SourceDetail />} />
      <Route path="sources/:id/edit" element={<SourceEdit />} />

      {/* Marketing */}
      <Route path="marketing" element={<MarketingPage />} />
      <Route path="marketing/:id" element={<MarketingCampaignDetail />} />

      {/* Offers */}
      <Route path="offers" element={<OffersList />} />
      <Route path="offers/new" element={<OfferNew />} />
      <Route path="offers/:id" element={<OfferDetail />} />
      <Route path="offers/:id/edit" element={<OfferEdit />} />

      {/* Financing */}
      <Route path="financing" element={<FinancingOverview />} />
      <Route path="financing/new" element={<Navigate to="/financing" replace />} />
      <Route path="financing/:id" element={<FinancingDetail />} />
      <Route path="financing/:id/edit" element={<FinancingEdit />} />
      <Route path="financing/lenders" element={<LendersList />} />
      <Route path="financing/lenders/new" element={<LenderNew />} />
      <Route path="financing/lenders/:id" element={<LenderDetail />} />
      <Route path="financing/lenders/:id/edit" element={<LenderEdit />} />

      {/* Markets */}
      <Route path="markets" element={<MarketsWatchlist />} />
      <Route path="markets/review" element={<MarketReviewQueue />} />
      <Route path="markets/compare" element={<MarketComparison />} />
      <Route path="markets/:slug" element={<MarketDetail />} />

      {/* Tasks */}
      <Route path="tasks" element={<TasksList />} />
      <Route path="tasks/new" element={<TaskNew />} />
      <Route path="tasks/:id" element={<TaskDetail />} />
      <Route path="tasks/:id/edit" element={<TaskEdit />} />

      {/* Diligence */}
      <Route path="diligence" element={<DiligenceOverview />} />

      {/* Contacts */}
      <Route path="contacts" element={<ContactsList />} />
      <Route path="contacts/new" element={<ContactNew />} />
      <Route path="contacts/triage" element={<ContactTriage />} />
      <Route path="marketing/outreach" element={<OutreachQueue />} />
      <Route path="marketing/outreach/print" element={<OutreachPrintRun />} />
      <Route path="owners" element={<OwnersDirectory />} />
      <Route path="owners/evidence" element={<OwnerEvidenceQueue />} />
      <Route path="contacts/:id" element={<ContactDetail />} />
      <Route path="contacts/:id/edit" element={<ContactEdit />} />

      {/* Reports */}
      <Route path="reports" element={<ReportsOverview />} />
      {/* The Pipeline / Source / Team / Win-Loss "reports" were hard-coded
          sample data that nothing linked to; their old URLs land on Reports. */}
      <Route path="reports/pipeline" element={<Navigate to="/reports" replace />} />
      <Route path="reports/sources" element={<Navigate to="/reports" replace />} />
      <Route path="reports/team" element={<Navigate to="/reports" replace />} />
      <Route path="reports/win-loss" element={<Navigate to="/reports" replace />} />
      {/* The offer funnel (Phase 3): requests → offers → accepted. Under
          /reports rather than /ops because it is a question about the last
          quarter, not a queue anyone works — /ops/offers is the queue. */}
      <Route path="reports/offers" element={<OffersReport />} />

      {/* Research. Development Opportunities deliberately has no Sidebar
          entry: active matching is a startsWith, so a second item under
          /research would light Market Research too (same reason Research
          Ops lives at /ops). ResearchDashboard links into it instead. */}
      <Route path="research" element={<ResearchDashboard />} />
      <Route path="research/development" element={<DevelopmentOpportunities />} />
      {/* Parcel Watch: same arrangement — reached from the Market Research header. */}
      <Route path="research/parcel-watch" element={<ParcelWatch />} />

      {/* Research Ops — the internal side of the researcher portal. Every
          page sits in OpsLayout, which draws the tabs that group them (the
          map is src/pages/ops/opsNav.ts). The paths are the ones notification
          emails, bells and bookmarks already use; none of them moved. */}
      <Route path="ops" element={<OpsLayout />}>
        <Route index element={<OpsDashboard />} />
        {/* Messages: one thread per researcher, and the note queue. */}
        <Route path="chat" element={<OpsChat />} />
        <Route path="notes" element={<OpsNotes />} />
        <Route path="review" element={<OpsReview />} />
        {/* The four desks. Under /ops and NOT Sidebar items: /offers already
            exists there for the CRM's own offer records, and active matching
            is a startsWith, so a sibling entry would light both. Each is a
            queue two people work, not a CRM record type. */}
        <Route path="letters" element={<OpsLetters />} />
        {/* `?id=<id>` opens one request's evidence panel — the action_url of
            every staff NDA bell. */}
        <Route path="ndas" element={<OpsNdas />} />
        <Route path="financials" element={<OpsFinancials />} />
        <Route path="offers" element={<OpsOffers />} />
        <Route path="call-center" element={<StaffCallCenter />} />
        {/* Team: the roster, what is assigned, and where. */}
        <Route path="researchers" element={<OpsResearchers />} />
        <Route path="researchers/:id" element={<ResearcherDetail />} />
        <Route path="researchers/:id/portal-view" element={<ResearcherPortalView />} />
        <Route path="assignments" element={<OpsAssignments />} />
        <Route path="territories" element={<TerritoriesAdmin />} />
        {/* Activity: what researchers did (feed), and the request-and-IP log
            (activity — the older path, kept for its links). */}
        <Route path="feed" element={<OpsActivityFeed />} />
        <Route path="activity" element={<OpsActivity />} />
      </Route>
      {/* Mention emails link here; the property page is the real target. A
          sibling of the layout, not a child: it only redirects, and should not
          draw the tabs or fetch their counts on the way through. */}
      <Route path="ops/properties/:propertyId" element={<OpsPropertyRedirect />} />

      {/* AI Imports */}
      <Route path="ai-imports" element={<AIImportsPage />} />
      {/* Static segment before :id so it isn't swallowed as a job id. */}
      <Route path="ai-imports/listing-review" element={<ListingReviewQueue />} />
      <Route path="ai-imports/:id" element={<AIImportDetail />} />

      {/* Email activity: every email Flow received or sent (read only) */}
      <Route path="email" element={<EmailActivityPage />} />

      {/* Settings */}
      <Route path="settings" element={<SettingsOverview />} />
      <Route path="settings/profile" element={<ProfileSettings />} />
      <Route path="settings/users" element={<UserManagement />} />
      <Route path="settings/criteria" element={<InvestmentCriteria />} />
      <Route path="settings/processes" element={<Navigate to="/settings" replace />} />
      <Route path="settings/tasks" element={<TaskTemplates />} />
      <Route path="settings/system" element={<Navigate to="/settings" replace />} />
      <Route path="settings/audit-log" element={<AuditLog />} />
      <Route path="settings/user-activity" element={<UserActivity />} />
      <Route path="settings/analytics" element={<SettingsAnalyticsDashboard />} />
      <Route path="settings/feedback" element={<FeedbackManagement />} />
      <Route path="settings/email-templates" element={<EmailTemplates />} />
      <Route path="settings/email-intake" element={<EmailIntake />} />

      {/* Admin Center */}
      <Route path="admin" element={<AdminCenter />} />
      <Route path="admin/ai-import-feedback" element={<AIImportFeedback />} />

      {/* What's New - Feature Log */}
      <Route path="whats-new" element={<FeatureLog />} />

      {/* Help Center */}
      <Route path="help" element={<HelpOverview />} />
      <Route path="help/faq" element={<FAQ />} />
      <Route path="help/glossary" element={<Glossary />} />
      <Route path="help/guide" element={<FeatureGuide />} />
      <Route path="help/guide/dashboard" element={<DashboardGuide />} />
      <Route path="help/guide/deals" element={<DealsGuide />} />
      <Route path="help/guide/funds" element={<FundsGuide />} />
      <Route path="help/guide/properties" element={<PropertiesGuide />} />
      <Route path="help/guide/buy-box" element={<BuyBoxGuide />} />
      <Route path="help/guide/portfolio" element={<PortfolioGuide />} />
      <Route path="help/guide/financial-modeling" element={<FinancialModelingGuide />} />
      <Route path="help/guide/property-checklist" element={<PropertyChecklistGuide />} />
      <Route path="help/guide/contacts" element={<ContactsGuide />} />
      <Route path="help/guide/offers" element={<OffersGuide />} />
      <Route path="help/guide/financing" element={<FinancingGuide />} />
      <Route path="help/guide/tasks" element={<TasksGuide />} />
      <Route path="help/guide/diligence" element={<DiligenceGuide />} />
      <Route path="help/guide/ic-meetings" element={<ICMeetingsGuide />} />
      <Route path="help/guide/analytics" element={<AnalyticsGuide />} />
      <Route path="help/guide/sources" element={<SourcesGuide />} />
      <Route path="help/guide/markets" element={<MarketsGuide />} />
      {/* OutreachGuide was written and exported but never routed — it was
          unreachable from anywhere in the app. */}
      <Route path="help/guide/outreach" element={<OutreachGuide />} />
      <Route path="help/guide/ai-import" element={<AIImportGuide />} />
      <Route path="help/guide/ai-assistant" element={<AIAssistantGuide />} />
      <Route path="help/guide/star-ratings" element={<StarRatingsGuide />} />
      <Route path="help/guide/owner-intelligence" element={<OwnerIntelligenceGuide />} />
      <Route path="help/guide/research-ops" element={<ResearchOpsGuide />} />
      <Route path="help/guide/property-lists" element={<PropertyListsGuide />} />
      <Route path="help/guide/documents-photos" element={<DocumentsPhotosGuide />} />
      <Route path="help/guide/sharing" element={<SharingGuide />} />
      <Route path="help/guide/development-opportunities" element={<DevelopmentOpportunitiesGuide />} />
    </Route>

    {/* Catch all - redirect to dashboard */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </>
);

// The same table drives preloading: the page the browser opened at starts
// downloading now, alongside the sign-in check rather than after it.
registerRoutes(createRoutesFromElements(appRoutes));
preloadRoute(window.location.pathname);

function App() {
  return (
    <BrowserRouter>
      <AnalyticsProvider>
        {/* At the root, so a <Tooltip> works on every surface — the app, the
            researcher portal and the public pages — not only inside the shell. */}
        <TooltipProvider>
          {/* Shown while a page outside the app frame downloads — the public
              pages and the portal. Pages inside the frame have their own
              boundary in AppShell, which keeps the sidebar on screen. */}
          <Suspense fallback={<PageSpinner fullScreen delayMs={150} />}>
            <Routes>{appRoutes}</Routes>
          </Suspense>
        </TooltipProvider>
        {/* Outside the routes, so a toast or a confirmation raised on a public
            page or in the researcher portal is drawn too. */}
        <Toaster />
        <ConfirmHost />
      </AnalyticsProvider>
    </BrowserRouter>
  );
}

export default App;
